import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { cn } from '../../../utils/cn';

export interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const WIDTH: Record<NonNullable<SlideOverProps['width']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export const SlideOver: React.FC<SlideOverProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  width = 'lg',
  footer,
  children,
}) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEntered(false);
      return;
    }
    const raf = requestAnimationFrame(() => setEntered(true));
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={cn('absolute inset-0 transition-opacity duration-300', entered ? 'opacity-100' : 'opacity-0')}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close panel"
      />
      <div
        className={cn(
          'relative flex h-full w-full flex-col shadow-2xl transition-transform duration-300 ease-out',
          WIDTH[width],
          entered ? 'translate-x-0' : 'translate-x-full',
        )}
        style={{ backgroundColor: 'var(--color-background-default)' }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          className="flex items-start justify-between gap-4 border-b px-6 py-4"
          style={{ borderColor: 'var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}
        >
          <div className="min-w-0">
            <h2 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {title}
            </h2>
            {subtitle && (
              <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-2 transition-colors hover:bg-[var(--color-hover-background)]"
            style={{ color: 'var(--color-text-secondary)' }}
            aria-label="Close panel"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: 'var(--color-border-light)', backgroundColor: 'var(--color-background-paper)' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
